/**
 * "Track stock per variant" on the product form: one row per variant with
 * its own par, its on hand (read-only — the ledger owns it) and Counted /
 * Received, which open the shared adjust modal with the variant fixed.
 * GRN/variants spec section 5.4.
 */
import React from 'react';
import type {VariantRow} from '../variantStock';

interface Props {
  rows: VariantRow[];
  /** variantConfig being edited, keyed by variantId. */
  pars: {[variantId: string]: {parLevel?: any}};
  productPar: any;
  /**
   * Adjusting needs the SAVED product to be tracked per variant: until then
   * the cloud would book a variant's count against the whole product.
   */
  canAdjust: boolean;
  onParChange: (variantId: string, value: string) => void;
  onAdjust: (row: VariantRow, mode: 'count' | 'receive') => void;
}

const STATUS_TEXT: Record<VariantRow['status'], string> = {
  ok: 'OK',
  low: 'Low',
  sold_out: 'Sold out',
  uncounted: 'Not counted',
};

const STATUS_STYLE: Record<VariantRow['status'], string> = {
  ok: 'text-emerald-700',
  low: 'text-amber-700',
  sold_out: 'text-red-700',
  uncounted: 'text-text3',
};

const VariantStockTable = ({rows, pars, productPar, canAdjust, onParChange, onAdjust}: Props) => (
  <div className="mt-3">
    <table className="w-full text-sm">
      <thead className="text-xs text-text3">
        <tr>
          <th className="py-1.5 text-left font-medium">Variant</th>
          <th className="py-1.5 text-right font-medium w-[90px]">Par</th>
          <th className="py-1.5 text-right font-medium w-[80px]">On hand</th>
          <th className="py-1.5 text-left font-medium pl-3 w-[90px]">Status</th>
          <th className="w-[150px]" />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.variantId} className="border-t border-borderc">
            <td className="py-1.5 text-text1">{row.label}</td>
            <td className="py-1.5 text-right">
              <input
                type="number"
                min="0"
                aria-label={`Par for ${row.label}`}
                placeholder={productPar != null && `${productPar}` !== '' ? `${productPar}` : '—'}
                value={`${pars[row.variantId]?.parLevel ?? ''}`}
                onChange={(e) => onParChange(row.variantId, e.target.value)}
                className="nl-input nl-input--sm w-[70px] text-right tabular-nums"
              />
            </td>
            <td className="py-1.5 text-right font-mono tabular-nums text-text1">
              {row.onHand ?? '—'}
            </td>
            <td className={`py-1.5 pl-3 text-xs font-medium ${STATUS_STYLE[row.status]}`}>
              {STATUS_TEXT[row.status]}
            </td>
            <td className="py-1.5">
              <div className="flex justify-end gap-1.5">
                {(['count', 'receive'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    disabled={!canAdjust}
                    title={canAdjust ? undefined : 'Save the product first'}
                    onClick={() => onAdjust(row, mode)}
                    className="nl-btn nl-btn--secondary nl-btn--xs"
                  >
                    {mode === 'count' ? 'Counted' : 'Received'}
                  </button>
                ))}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="mt-2 text-xs text-text3">
      A blank par uses the product&apos;s par level. Each variant starts uncounted;
      count it once to begin.
    </p>
  </div>
);

export default VariantStockTable;
